import React, { useState, useEffect, useRef, useCallback } from 'react';

export function useZoomPan(baseViewBox) {
  const [vb, setVb] = React.useState(baseViewBox);
  const gestureRef = useRef(null);
  const vbRef = useRef(baseViewBox);
  vbRef.current = vb;
  const tapRef = useRef(null);
  const onTapRef = useRef(null);
  const maxZoom = 5;
  const handlers = useRef({
    onTouchStart(e) {
      if (e.touches.length === 2) {
        e.preventDefault();
        const t = e.touches;
        const dist = Math.hypot(t[0].clientX-t[1].clientX, t[0].clientY-t[1].clientY);
        gestureRef.current = { type:'pinch', startDist:dist, startVb:{...vbRef.current} };
      } else if (e.touches.length === 1) {
        tapRef.current = { x:e.touches[0].clientX, y:e.touches[0].clientY, t:Date.now() };
        const cur = vbRef.current;
        if (cur.w < baseViewBox.w*0.99 || cur.h < baseViewBox.h*0.99) {
          e.preventDefault();
          gestureRef.current = { type:'pan', startX:e.touches[0].clientX, startY:e.touches[0].clientY, startVb:{...cur} };
        }
      }
    },
    onTouchMove(e) {
      const g = gestureRef.current; if (!g) return;
      if (g.type==='pinch' && e.touches.length===2) {
        e.preventDefault();
        const t = e.touches;
        const dist = Math.hypot(t[0].clientX-t[1].clientX, t[0].clientY-t[1].clientY);
        const scale = g.startDist/dist;
        let newW = Math.max(baseViewBox.w/maxZoom, Math.min(baseViewBox.w, g.startVb.w*scale));
        let newH = Math.max(baseViewBox.h/maxZoom, Math.min(baseViewBox.h, g.startVb.h*scale));
        let newX = g.startVb.x+(g.startVb.w-newW)/2;
        let newY = g.startVb.y+(g.startVb.h-newH)/2;
        newX = Math.max(baseViewBox.x, Math.min(baseViewBox.x+baseViewBox.w-newW, newX));
        newY = Math.max(baseViewBox.y, Math.min(baseViewBox.y+baseViewBox.h-newH, newY));
        setVb({ x:newX, y:newY, w:newW, h:newH });
      } else if (g.type==='pan' && e.touches.length===1) {
        e.preventDefault();
        const dx = e.touches[0].clientX-g.startX, dy = e.touches[0].clientY-g.startY;
        const svg = e.currentTarget, rect = svg.getBoundingClientRect();
        const scaleX = g.startVb.w/rect.width, scaleY = g.startVb.h/rect.height;
        let newX = g.startVb.x-dx*scaleX, newY = g.startVb.y-dy*scaleY;
        newX = Math.max(baseViewBox.x, Math.min(baseViewBox.x+baseViewBox.w-g.startVb.w, newX));
        newY = Math.max(baseViewBox.y, Math.min(baseViewBox.y+baseViewBox.h-g.startVb.h, newY));
        setVb({ x:newX, y:newY, w:g.startVb.w, h:g.startVb.h });
      }
    },
    onTouchEnd(e) {
      if (e.touches.length===0) {
        const tap = tapRef.current;
        if (tap && gestureRef.current?.type!=='pinch') {
          const dt = Date.now()-tap.t, ct = e.changedTouches[0];
          if (dt<300 && Math.abs(ct.clientX-tap.x)<10 && Math.abs(ct.clientY-tap.y)<10 && onTapRef.current)
            onTapRef.current(ct.clientX, ct.clientY);
        }
        tapRef.current = null; gestureRef.current = null;
      } else if (e.touches.length===1 && gestureRef.current?.type==='pinch') {
        tapRef.current = null;
        gestureRef.current = { type:'pan', startX:e.touches[0].clientX, startY:e.touches[0].clientY, startVb:{...vbRef.current} };
      }
    },
  }).current;
  const reset = useCallback(() => setVb(baseViewBox), [baseViewBox]);
  const isZoomed = vb.w < baseViewBox.w*0.99;
  const zoomLevel = Math.round(baseViewBox.w/vb.w*10)/10;
  const setOnTap = useCallback((fn) => { onTapRef.current = fn; }, []);
  return { vb, setVb, handlers, reset, isZoomed, zoomLevel, setOnTap };
}

export function PointInspector({ point, scoringCtx, chartType, onClose }) {
  if (!point) return null;
  const gustAdd = Math.max(0, Math.floor(((scoringCtx?.wind?.gust||0) - (scoringCtx?.wind?.wind_speed||0)) / 2));
  const tgt = (scoringCtx?.targetSpeed || 70) + gustAdd;
  const wind = scoringCtx?.wind || {};
  const flags = point.flags || [];
  const flagLabels = {
    gs_danger_low:['\ud83d\udd34','Dangerously Low'], gs_warn_low:['\ud83d\udfe1','Below Glideslope'],
    gs_high:['\ud83d\udfe1','Above Glideslope'], cl_severe:['\ud83d\udd34','Far Off Centerline'],
    cl_warn:['\ud83d\udfe1','Off Centerline'], speed_fast:['\ud83d\udfe1','Too Fast'],
    speed_slow:['\ud83d\udfe1','Too Slow'], bank_steep:['\ud83d\udd34','Steep Bank'], climbing:['\ud83d\udfe1','Climbing'],
  };
  return (
    <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:9999, background:'var(--bg-card, #1a1a2e)', borderTop:'1px solid var(--border, #333)', borderRadius:'16px 16px 0 0', padding:'12px 16px 24px', boxShadow:'0 -4px 24px rgba(0,0,0,0.5)', animation:'slideUp 0.2s ease-out' }}>
      <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <div style={{ width:36, height:4, background:'#555', borderRadius:2, margin:'0 auto' }} />
        {point.i!=null && <span style={{ position:'absolute', left:16, top:14, fontSize:15, color:'#666', fontFamily:'var(--font-mono)' }}>Pt {point.i+1}</span>}
        <button onClick={onClose} style={{ position:'absolute', right:16, top:12, background:'none', border:'none', color:'#888', fontSize:22, cursor:'pointer', padding:4 }}>×</button>
      </div>
      {point.inScoringWindow===false && (
        <div style={{ fontSize:14, color:'#888', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'6px 10px', marginBottom:8 }}>
          ℹ️ Outside scoring window — not evaluated
        </div>
      )}
      {point._synthetic && (
        <div style={{ fontSize:14, color:'#94a3b8', background:'rgba(148,163,184,0.07)', border:'1px solid rgba(148,163,184,0.18)', borderRadius:8, padding:'6px 10px', marginBottom:8 }}>
          ∼ Interpolated — no raw ADS-B fix at this position
        </div>
      )}
      {flags.length>0 && point.inScoringWindow!==false && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
          {flags.map((f,i) => {
            const [icon,label] = flagLabels[f]||['\u26a0\ufe0f',f];
            return <span key={i} style={{ fontSize:17, padding:'4px 10px', borderRadius:12, background:icon==='\ud83d\udd34'?'rgba(239,68,68,0.15)':'rgba(234,179,8,0.15)', color:icon==='\ud83d\udd34'?'#f87171':'#fbbf24', border:`1px solid ${icon==='\ud83d\udd34'?'rgba(239,68,68,0.3)':'rgba(234,179,8,0.3)'}` }}>{icon} {label}</span>;
          })}
        </div>
      )}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px 16px', fontSize:18 }}>
        <div><div style={{ color:'#888', fontSize:16 }}>Distance</div><div style={{ fontFamily:'var(--font-mono)', fontWeight:600 }}>{Math.abs(point.distNm).toFixed(1)} nm</div></div>
        <div><div style={{ color:'#888', fontSize:16 }}>AGL</div><div style={{ fontFamily:'var(--font-mono)', fontWeight:600 }}>{point.agl?.toLocaleString()}ft</div></div>
        {chartType==='vertical' && (<>
          <div><div style={{ color:'#888', fontSize:16 }}>GS Deviation</div><div style={{ fontFamily:'var(--font-mono)', fontWeight:600, color:point.gsDevFt<-200?'#f87171':point.gsDevFt<-100?'#fbbf24':'#10b981' }}>{point.gsDevFt>0?'+':''}{point.gsDevFt}ft</div></div>
          <div><div style={{ color:'#888', fontSize:16 }}>V/S</div><div style={{ fontFamily:'var(--font-mono)', fontWeight:600 }}>{point.vs!=null?`${point.vs} fpm`:'—'}</div></div>
        </>)}
        {chartType==='horizontal' && (<>
          <div><div style={{ color:'#888', fontSize:16 }}>CL Offset</div><div style={{ fontFamily:'var(--font-mono)', fontWeight:600, color:Math.abs(point.crossTrackFt)>500?'#f87171':Math.abs(point.crossTrackFt)>300?'#fbbf24':'#10b981' }}>{point.crossTrackFt>0?'R':'L'} {Math.abs(point.crossTrackFt)}ft</div></div>
          <div><div style={{ color:'#888', fontSize:16 }}>Bank Angle</div><div style={{ fontFamily:'var(--font-mono)', fontWeight:600, color:point.bank>30?'#f87171':'#ccc' }}>{point.bank!=null?`${point.bank}°`:'—'}</div></div>
        </>)}
        <div><div style={{ color:'#888', fontSize:16 }}>IAS / Target</div><div style={{ fontFamily:'var(--font-mono)', fontWeight:600 }}><span style={{ color:point.speedDev!=null&&Math.abs(point.speedDev)>7?(point.speedDev>0?'#fbbf24':'#f87171'):'#10b981' }}>{point.ias!=null?`${point.ias}kt`:'—'}</span><span style={{ color:'#666' }}> / {tgt}kt</span></div></div>
        <div><div style={{ color:'#888', fontSize:16 }}>GS / Headwind</div><div style={{ fontFamily:'var(--font-mono)', fontWeight:600 }}>{point.speed!=null?`${point.speed}kt`:'—'}<span style={{ color:'#666' }}> / {wind.headwind||0}kt</span></div></div>
      </div>
      {point.time && <div style={{ marginTop:8, fontSize:15, color:'#555' }}>{point.time}</div>}
    </div>
  );
}

function VerticalProfile({ points, runway, analysis, synthProf, scoringContext }) {
  const canvasRef = useRef(null);
  const [selectedPt, setSelectedPt] = useState(null);
  const [showFull, setShowFull] = useState(false);
  const [showSynth, setShowSynth] = useState(true);
  const dataRef = useRef([]);
  const pointerDownRef = useRef(null);

  if (!points || points.length < 3 || !runway) return null;

  const rwElev = runway.elevation || 0;
  const gs = runway.glideslope || 3.0;
  const rwLat = parseFloat(runway.threshold_lat);
  const rwLon = parseFloat(runway.threshold_lon);
  const apprType = scoringContext?.approachType || {};
  const apprLabel = apprType.label || '';
  const thresh = scoringContext?.thresholds || {};
  const gsWarn = thresh.gsWarnBelow || -150;
  const gsDanger = thresh.gsDangerBelow || -275;
  const gsHighBase = thresh.gsHighAbove || 200;



  // Prefer engine-generated synthProf (perfectly filtered inbound points).
  // Fall back to server analysis for older rows that predate synthProf storage.
  const src = (synthProf && synthProf.length > 3) ? synthProf : analysis;
  if (!src || src.length < 3) return null;
  const isSynth = !!(synthProf && synthProf.length > 3);
  const data = src
    .filter(a => a.distNm >= -0.1 && a.distNm <= 12 && a.agl >= -200 && a.agl <= 5000)
    .sort((a, b) => a.distNm - b.distNm)
    .map(a => ({ dist: a.distNm, alt: a.agl, speed: a.speed, synthetic: a.synthetic, _analysis: isSynth ? null : a }));
  if (data.length < 3) return null;

  dataRef.current = data;
  const maxDist = Math.min(12, Math.ceil(Math.max(...data.map(d=>d.dist))));
  const X_MAX = showFull ? maxDist : Math.min(5, maxDist);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const dpr = window.devicePixelRatio||1;
    const W = cv.parentElement.clientWidth||320;
    const H = 220;
    cv.width = Math.round(W*dpr); cv.height = Math.round(H*dpr);
    cv.style.width = W+'px'; cv.style.height = H+'px';
    const ctx = cv.getContext('2d'); ctx.scale(dpr,dpr);

    const ml=44, mr=12, mt=18, mb=30;
    const pw=W-ml-mr, ph=H-mt-mb;
    const Y_MIN=-100, Y_MAX=Math.max(1800, Math.ceil(Math.max(...data.filter(d=>d.dist<=X_MAX).map(d=>d.alt))/200)*200+200);
    const Y_RNG=Y_MAX-Y_MIN;
    const xPx=nm=>ml+pw*(1-nm/X_MAX);
    const yPx=ft=>mt+ph*(1-(ft-Y_MIN)/Y_RNG);
    const yc=ft=>Math.max(mt,Math.min(mt+ph,yPx(ft)));
    const FAP=1.0, FAF=3.5;

    ctx.fillStyle='#0d0f14'; ctx.fillRect(0,0,W,H);

    function fillBand(x0,x1,botFn,topFn,color,alpha){
      const N=60,step=(x1-x0)/N;
      ctx.save(); ctx.globalAlpha=alpha; ctx.fillStyle=color; ctx.beginPath();
      for(let i=0;i<=N;i++){const nm=x0+i*step,px=xPx(nm),py=yc(topFn(nm));i===0?ctx.moveTo(px,py):ctx.lineTo(px,py);}
      for(let i=N;i>=0;i--){ctx.lineTo(xPx(x0+i*step),yc(botFn(x0+i*step)));}
      ctx.closePath(); ctx.fill(); ctx.restore();
    }
    // Zone fills — match flight_analyzer exactly
    // gsAgl: pure slope from origin (used for zone boundaries)
    // gsLine: slope + 25ft TCH (used for the dashed GS line only)
    const gsAgl=nm=>Math.tan(gs*Math.PI/180)*nm*6076.12;
    const UP=nm=>100+nm*(2000-100)/3.5;
    const isIFR = apprType.model==='IFR';
    const fap=Math.min(FAP,X_MAX), x0=Math.min(FAF,X_MAX);
    // IFR: fill FAP->FAF with flat 400ft floor + upper envelope
    if(isIFR && x0>fap){
      fillBand(fap,x0,()=>400,UP,'#22c55e',0.12);
      fillBand(fap,x0,()=>0,()=>400,'#ef4444',0.10);
    }
    // All: fill 0->FAP with GS-relative zones
    fillBand(0,fap,gsAgl,UP,'#22c55e',0.12);
    fillBand(0,fap,()=>0,gsAgl,'#ef4444',0.10);
    // VFR: amber zone above upper envelope
    if(!isIFR){ fillBand(0,fap,UP,()=>1200,'#eab308',0.09); }

    ctx.save(); ctx.strokeStyle='#22c55e'; ctx.lineWidth=2;
    ctx.beginPath(); ctx.moveTo(ml,yPx(0)); ctx.lineTo(ml+pw,yPx(0)); ctx.stroke(); ctx.restore();

    const yStep=Y_MAX<=800?100:200;
    for(let ft=Math.ceil(Y_MIN/yStep)*yStep;ft<=Y_MAX;ft+=yStep){
      const y=yPx(ft); if(ft<Y_MIN||ft>Y_MAX) continue;
      ctx.strokeStyle=ft===0?'rgba(255,255,255,.22)':'#1e2230'; ctx.lineWidth=ft===0?1.5:0.75;
      ctx.beginPath(); ctx.moveTo(ml,y); ctx.lineTo(ml+pw,y); ctx.stroke();
      if(ft>=0&&ft%(yStep*2)===0||ft===0){
        ctx.fillStyle=ft===0?'#22c55e':'#4a5568'; ctx.font='9px monospace';
        ctx.textAlign='right'; ctx.fillText(ft===0?'0':ft+'ft',ml-4,y+3);
      }
    }

    [{nm:3.5,label:'FAF',col:'rgba(255,255,255,.45)'},{nm:1.0,label:'FAP',col:'rgba(255,255,255,.45)'},{nm:0.5,label:'VDES',col:'rgba(234,179,8,.65)'}].forEach(({nm,label,col})=>{
      if(nm>X_MAX) return;
      const x=xPx(nm);
      ctx.save(); ctx.strokeStyle=col; ctx.lineWidth=1; ctx.setLineDash([4,3]);
      ctx.beginPath(); ctx.moveTo(x,mt); ctx.lineTo(x,mt+ph); ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle=col; ctx.font='bold 9px monospace'; ctx.textAlign='right'; ctx.textBaseline='top';
      ctx.fillText(label,x-2,mt+2); ctx.textBaseline='alphabetic'; ctx.restore();
    });

    for(let nm=0;nm<=X_MAX;nm++){
      const x=xPx(nm); ctx.strokeStyle='#1a1e26'; ctx.lineWidth=0.75;
      ctx.beginPath(); ctx.moveTo(x,mt); ctx.lineTo(x,mt+ph); ctx.stroke();
      ctx.fillStyle='#4a5568'; ctx.font='9px monospace'; ctx.textAlign='center';
      ctx.fillText(nm===0?'THR':'-'+nm,x,mt+ph+14);
    }

    // GS dashed line: 25ft TCH + 300ft/nm slope (matches flight_analyzer)
    const gsLineFn=nm=>25+nm*300;
    ctx.save(); ctx.strokeStyle='rgba(234,179,8,.7)'; ctx.lineWidth=1.5; ctx.setLineDash([6,4]);
    const gsMax=Math.min(X_MAX,(Y_MAX-25)/300);
    ctx.beginPath(); ctx.moveTo(xPx(0),yPx(gsLineFn(0))); ctx.lineTo(xPx(gsMax),yPx(gsLineFn(gsMax))); ctx.stroke(); ctx.restore();

    const visData = data.filter(d=>d.dist<=X_MAX && (showSynth || !d.synthetic));
    ctx.save(); ctx.strokeStyle='rgba(59,130,246,.5)'; ctx.lineWidth=1.5; ctx.lineJoin='round';
    ctx.beginPath();
    visData.forEach((d,i)=>{ const x=xPx(d.dist),y=yc(d.alt); i===0?ctx.moveTo(x,y):ctx.lineTo(x,y); });
    ctx.stroke(); ctx.restore();

    visData.forEach(d=>{
      const gsAtDist=Math.tan(gs*Math.PI/180)*d.dist*6076.12;
      const dev=d.alt-gsAtDist;
      const col=dev<gsDanger?'#ef4444':dev<gsWarn?'#eab308':'#3b82f6';
      const r=d._analysis?2.5:2;
      ctx.beginPath(); ctx.arc(xPx(d.dist),yc(d.alt),r,0,Math.PI*2);
      if(d.synthetic){
        ctx.save(); ctx.globalAlpha=0.38; ctx.strokeStyle=col; ctx.lineWidth=1; ctx.stroke(); ctx.restore();
      } else {
        ctx.fillStyle=col; ctx.fill();
      }
    });

    if(selectedPt){
      ctx.beginPath(); ctx.arc(xPx(selectedPt.distNm),yc(selectedPt.agl),6,0,Math.PI*2);
      ctx.fillStyle='rgba(96,165,250,0.3)'; ctx.strokeStyle='#60a5fa'; ctx.lineWidth=2; ctx.fill(); ctx.stroke();
    }

    const txcPt = data.find(d=>d.dist<0.15);
    if(txcPt){
      const xThr=xPx(txcPt.dist), yThr=yc(txcPt.alt);
      ctx.save(); ctx.fillStyle='rgba(251,146,60,.95)';
      ctx.beginPath(); ctx.moveTo(xThr-8,yThr-12); ctx.lineTo(xThr+8,yThr-12); ctx.lineTo(xThr,yThr); ctx.closePath(); ctx.fill();
      ctx.fillStyle='rgba(251,146,60,1)'; ctx.font='bold 9px monospace'; ctx.textAlign='center'; ctx.textBaseline='bottom';
      ctx.fillText(Math.round(txcPt.alt)+'ft',xThr,yThr-13); ctx.textBaseline='alphabetic'; ctx.restore();
    }

    ctx.fillStyle='rgba(255,255,255,.3)'; ctx.font='bold 10px monospace';
    ctx.textAlign='left'; ctx.textBaseline='top';
    ctx.fillText('VERTICAL PROFILE · '+(showFull?maxDist:Math.min(5,maxDist))+'nm', ml+4, mt+3);
    ctx.textBaseline='alphabetic';
    ctx.fillStyle='rgba(255,255,255,.3)'; ctx.font='9px monospace'; ctx.textAlign='right'; ctx.textBaseline='top';
    ctx.fillText('GS '+gs+'°', ml+pw-2, mt+3); ctx.textBaseline='alphabetic';

  }, [data, showFull, showSynth, selectedPt, gs, X_MAX, gsDanger, gsWarn]);

  const handlePointerDown = e => { pointerDownRef.current={x:e.clientX,y:e.clientY,t:Date.now()}; };
  const handlePointerUp = e => {
    const down=pointerDownRef.current; if(!down) return; pointerDownRef.current=null;
    const dt=Date.now()-down.t, dx=Math.abs(e.clientX-down.x), dy=Math.abs(e.clientY-down.y);
    if(dt<400&&dx<15&&dy<15){
      const cv=canvasRef.current; if(!cv) return;
      const rect=cv.getBoundingClientRect();
      const dpr=window.devicePixelRatio||1;
      const W=cv.parentElement.clientWidth||320, H=220;
      const ml=44,mr=12,mt=18,mb=30, pw=W-ml-mr, ph=H-mt-mb;
      const Y_MIN=-100, Y_MAX=Math.max(1800,Math.ceil(Math.max(...dataRef.current.filter(d=>d.dist<=X_MAX).map(d=>d.alt))/200)*200+200);
      const Y_RNG=Y_MAX-Y_MIN;
      const xPx=nm=>ml+pw*(1-nm/X_MAX);
      const yPx=ft=>mt+ph*(1-(ft-Y_MIN)/Y_RNG);
      const clickX=((e.clientX-rect.left)/rect.width)*W;
      const clickY=((e.clientY-rect.top)/rect.height)*H;
      let closest=null, closestD=Infinity;
      dataRef.current.filter(d=>d.dist<=X_MAX && (showSynth || !d.synthetic)).forEach(d=>{
        const dd=Math.sqrt((xPx(d.dist)-clickX)**2+(yPx(d.alt)-clickY)**2);
        if(dd<closestD){closestD=dd;closest=d;}
      });
      if(closest&&closestD<35){
        if(closest._analysis) setSelectedPt(closest._analysis);
        else setSelectedPt({distNm:closest.dist,agl:closest.alt,speed:closest.speed||0,ias:closest.speed!=null?Math.round(closest.speed+(scoringContext?.wind?.headwind||0)):null,speedDev:null,gsDevFt:Math.round(closest.alt-(Math.tan(gs*Math.PI/180)*closest.dist*6076.12)),crossTrackFt:0,flags:[],_synthetic:closest.synthetic});
      }
    }
  };

  return (
    <div style={{ marginTop:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
        <div className="section-title" style={{ display:'flex', alignItems:'center', gap:8 }}>
          Vertical Profile
          {apprLabel && <span style={{ fontSize:12, padding:'2px 7px', borderRadius:10, background:apprType.model==='IFR'?'rgba(99,102,241,0.15)':'rgba(16,185,129,0.15)', color:apprType.model==='IFR'?'#818cf8':'#10b981', fontWeight:600 }}>{apprLabel}</span>}
        </div>
        <div style={{ display:'flex', gap:6 }}>
          <button onClick={()=>setShowSynth(s=>!s)} style={{ fontSize:13, padding:'4px 10px', borderRadius:16, cursor:'pointer', border:`1px solid ${showSynth?'rgba(234,179,8,0.5)':'rgba(100,116,139,0.3)'}`, background:showSynth?'rgba(234,179,8,0.12)':'rgba(100,116,139,0.08)', color:showSynth?'#fbbf24':'#64748b', fontWeight:600 }}>{showSynth?'All pts':'Real only'}</button>
          {maxDist>5 && <button onClick={()=>setShowFull(f=>!f)} style={{ fontSize:13, padding:'4px 10px', borderRadius:16, cursor:'pointer', border:'1px solid rgba(16,185,129,0.4)', background:'rgba(16,185,129,0.1)', color:'#10b981', fontWeight:600 }}>{showFull?'5nm':`${maxDist}nm`}</button>}
          {selectedPt && <button onClick={()=>setSelectedPt(null)} style={{ fontSize:13, padding:'4px 10px', borderRadius:16, cursor:'pointer', border:'1px solid #3b82f6', background:'rgba(59,130,246,0.15)', color:'#60a5fa', fontWeight:600 }}>× Close</button>}
        </div>
      </div>
      <div style={{ borderRadius:8, overflow:'hidden', border:`1px solid ${selectedPt?'rgba(59,130,246,0.4)':'var(--border)'}` }}>
        <canvas ref={canvasRef} style={{ display:'block', width:'100%', cursor:'crosshair', touchAction:'none' }}
          onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} />
      </div>
      <div style={{ textAlign:'center', fontSize:13, color:'#555', padding:'4px 0 0' }}>Tap point for details</div>
      {selectedPt && <PointInspector point={selectedPt} scoringCtx={scoringContext} chartType="vertical" onClose={()=>setSelectedPt(null)} />}
    </div>
  );
}

export { VerticalProfile };
export default VerticalProfile;
